import { useCallback, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import {
  UseRefinementListProps,
  useInstantSearch,
  useRefinementList,
  useClearRefinements,
  useCurrentRefinements,
} from 'react-instantsearch';
import { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';

import useTabVisibility from '@hooks/useTabVisibility';

import DropdownMenu from '@components/ui/DropdownMenu';
import { parseAlgoliaUrl } from '@utils/algolia/helpers';
import SizePanel from '@components/algolia/SearchResults/Sidebar/SizePanel';

import styles from './SizeBucketRefinement.module.scss';

type RawResult = { facets?: Record<string, Record<string, number>> };

type InstantSearchResults = {
  results: {
    _rawResults?: RawResult[];
    disjunctiveFacets?: { name: string }[];
  };
};

/**
 * Shoe-size bucket names (`size.<bucket>`) available for the current results.
 *
 * `size.*` facets are requested as wildcard facets but deliberately kept out of
 * the DynamicWidgets list (see `makeFacetTransformItems`), so no RefinementList
 * is mounted for them and the helper never files them onto
 * `results.disjunctiveFacets`. Read the raw Algolia response as the primary
 * source, and keep the disjunctiveFacets read for buckets that are mounted
 * (e.g. the one this widget is currently refining).
 */
const bucketNames = (results: InstantSearchResults['results']): string[] => {
  const buckets = new Set<string>();

  const collect = (facetName: string) => {
    const fragments = facetName.split('size.');
    if (fragments.length > 1) buckets.add(fragments[1]);
  };

  for (const raw of results?._rawResults ?? []) {
    Object.keys(raw.facets ?? {}).forEach(collect);
  }
  for (const facet of results?.disjunctiveFacets ?? []) {
    collect(facet.name);
  }

  return [...buckets];
};

const SizeBucketRefinement = (
  props: UseRefinementListProps & {
    locale?: string;
    onSizeSelected: () => void;
  }
) => {
  const router = useRouter();
  const currentUrl = router.asPath;
  const { isGenderedMale, isGenderedFemale } = parseAlgoliaUrl(
    currentUrl,
    'en'
  );

  const plpGender = isGenderedMale
    ? 'mens'
    : isGenderedFemale
    ? 'womens'
    : null;

  const { onSizeSelected } = props;
  const parseHelper = (label: string) => {
    if (!label) return 0;
    const parts = label.split(';');
    const correction = parts.length > 1 ? 0.5 : 0;

    // We want to show these at the top of the facet, if they are present
    if (
      label.toLowerCase().includes('infant') ||
      label.toLowerCase().includes('bébé')
    ) {
      return -1000;
    }

    return parseFloat(parts[0]) + correction;
  };

  const sortDescending = useCallback((arr: RefinementListItem[]) => {
    return arr.sort((a, b) => {
      return parseHelper(a.value) - parseHelper(b.value);
    });
  }, []);

  const clearButtonRef = useRef<HTMLButtonElement>(null);

  useTabVisibility(clearButtonRef);

  const results = useInstantSearch() as unknown as InstantSearchResults;

  const options: string[] = bucketNames(results.results);

  const { t } = useTranslation(['searchResults']);

  // These are the bucket names we are using right now, and we want them to appear in this order (if they are present)
  // If we have bucket names besides these, they will work, but they will appear at the end of the list
  const namePriorityList = [
    'Mondopoint',
    "US Men's",
    'US Hommes',
    "US Women's",
    'US Femmes',
    'EU',
    'Infants',
    'Bébés',
    'Kids',
    'Enfants',
    'Youth',
    'Jeunes',
    'Waist',
    'Alphabetical',
  ];

  const newDropdownOptions = options
    .sort((a, b) => {
      const aIndex = namePriorityList.indexOf(a);
      const aScore = aIndex === -1 ? 10000 : aIndex;
      const bIndex = namePriorityList.indexOf(b);
      const bScore = bIndex === -1 ? 10000 : bIndex;
      return aScore - bScore;
    })
    .filter((option) => {
      // Because unisex shoes are sized in mens and womens, we end up having some mens sizes on women specific plps
      // And vice versa - this is confusing, especially with the dropdown defaults to women
      // Detect this case and skip those gendered size types for gendered PLPs
      if (
        plpGender &&
        plpGender === 'mens' &&
        option.toLowerCase().includes('women')
      ) {
        return false;
      }
      if (
        plpGender &&
        plpGender === 'womens' &&
        // not a typo
        option.toLowerCase().includes(' men')
      ) {
        return false;
      }
      if (
        plpGender &&
        plpGender === 'mens' &&
        option.toLowerCase().includes('femmes')
      ) {
        return false;
      }
      if (
        plpGender &&
        plpGender === 'womens' &&
        option.toLowerCase().includes('hommes')
      ) {
        return false;
      }
      return true;
    })
    .map((option) => ({
      name: option,
      value: option,
      disabled: false,
    }));

  type DropdownOption = {
    name: string;
    value: string;
    disabled: boolean;
  };

  let preset = newDropdownOptions[0]?.name ?? '';

  // There might already be a width refined on initial page load, which would come from the URL.
  // If there is, work out which, if any of our available width names correspond to it and use that in the dropdown
  for (const sizeOption of options) {
    if ([''].includes(sizeOption)) {
      preset = sizeOption;
    }
  }

  const [bucketName, setBucketName] = useState(preset);
  const refinementName = `size.${bucketName}`;

  const [currentDropdownSetting, setCurrentDropdownSetting] =
    useState<string>(preset);

  // If no valid size bucket facets being returned, just use the previously selected one to avoid an empty dropdown
  const dropdownOptions: DropdownOption[] =
    newDropdownOptions.length > 0
      ? newDropdownOptions
      : [
          {
            name: currentDropdownSetting,
            value: currentDropdownSetting,
            disabled: false,
          },
        ];

  // If the currentDropdownSetting is no longer in the list of possible options, choose from the top of that list
  // This can happen if a refinement change by another component makes it so the currently selected dropdown now
  // has no hits in the page. Sneaky case, since the HTML select will look like its changed, but it actually isn't
  if (
    !dropdownOptions.some((option) => option.value === currentDropdownSetting)
  ) {
    const firstNewOption = dropdownOptions[0].value;
    setCurrentDropdownSetting(firstNewOption);
    setBucketName(firstNewOption);
  }

  const widthRefinement = useRefinementList({
    attribute: 'shoeWidth',
    limit: 100,
    operator: 'or',
    transformItems: sortDescending,
  });

  const refineWidth = widthRefinement?.refine;

  const availableWidths =
    widthRefinement?.items.map((item) => item.label) ?? [];

  // if the size dropdown has one of these values, then skip the width dropdown
  const widthlessSizeBuckets = [
    'Kids',
    'Jeunes',
    'Youth',
    'Enfants',
    'Infants',
    'Bébés',
    'Mondopoint',
  ];

  // Check for only Regular available, or nothing available
  let shouldShowWidthDropdown = !(
    !availableWidths ||
    (availableWidths.length === 1 &&
      (availableWidths[0] === 'Regular' || availableWidths[0] === 'Régulier'))
  );

  if (widthlessSizeBuckets.includes(bucketName)) {
    shouldShowWidthDropdown = false;
  }

  const defaultText = props.locale === 'fr' ? 'Tout' : 'All';

  const widthOptions = [defaultText].concat(availableWidths).map((option) => ({
    name: option,
    value: option,
    disabled: false,
  }));

  const { items, refine } = useRefinementList({
    attribute: refinementName,
    limit: 100,
    operator: 'or',
    transformItems: sortDescending,
  });

  let widthName = widthOptions[0]?.name ?? '';

  const { items: currentRefinements } = useCurrentRefinements();

  const selectedWidthFilters = currentRefinements
    .filter((refinement) => refinement.label === 'shoeWidth')
    .map((refinement) => refinement.refinements[0].label);

  // Assume that only one width facet is possible at once
  if (selectedWidthFilters.length > 0) {
    widthName = selectedWidthFilters[0];
  }

  const clearSizeRefinements = useClearRefinements({
    includedAttributes: [refinementName],
  });

  const clearWidthRefinements = useClearRefinements({
    includedAttributes: ['shoeWidth'],
  });

  return (
    <>
      <div className={styles.sizeDropdownContainer}>
        <DropdownMenu
          id="size-bucket-select"
          defaultText={t('search.facet.size_dropdown_default')}
          selectedOption={bucketName}
          data={dropdownOptions}
          handleChange={(value: string) => {
            setBucketName(value);
            clearSizeRefinements.refine();
          }}
          hasError={false}
        />
      </div>
      {shouldShowWidthDropdown && (
        <div className={styles.shoeWidthTitle}>
          {' '}
          {t('search.facet.shoeWidth')}{' '}
        </div>
      )}
      {shouldShowWidthDropdown && (
        <div className={styles.sizeDropdownContainer}>
          <DropdownMenu
            id="size-width-select"
            defaultText={t('search.facet.width')}
            selectedOption={widthName}
            data={widthOptions}
            handleChange={(value: string) => {
              clearWidthRefinements.refine();
              if (value !== defaultText && !!refineWidth) {
                refineWidth(value);
              }
            }}
            hasError={false}
          />
        </div>
      )}
      <div className={styles.sizePanelContainer}>
        {items.map((item) => (
          <SizePanel
            key={`size-panel-${item.value}`}
            label={item.label}
            handleClick={(e) => {
              e.preventDefault();
              onSizeSelected();
              const previousFilters = items
                .filter((refinementListItem) => refinementListItem.isRefined)
                .map((refinementListItem) => refinementListItem.value);
              setCurrentDropdownSetting(bucketName);
              clearSizeRefinements.refine();
              if (!previousFilters.includes(item.value)) {
                refine(item.value);
              }
            }}
            selected={item.isRefined}
          />
        ))}
      </div>
    </>
  );
};

export default SizeBucketRefinement;
